'use client'

import CommunityCard from '@/components/_cards/community/community-card'
import GameDiscoverySearchForm from '@/components/search/game-discovery-search-form'
import { draftSignature } from '@/components/search/search-filter-helpers'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useCallback, useEffect, useRef, useState } from 'react'
import { loadMoreCommunities } from '../../actions/load-more'

export default function CommunitiesClientPage({
  game,
  gameName,
  appliedFilter,
  initialItems,
  initialTotal,
  initialNextCursor,
}) {
  const loaderRef = useRef(null)
  const currentFilter = useRef(null)
  currentFilter.current = draftSignature(appliedFilter)
  const loadingRequest = useRef(null)
  const [loadError, setLoadError] = useState('')

  const [items, setItems] = useState(initialItems)
  const [total, setTotal] = useState(initialTotal)
  const [nextCursor, setNextCursor] = useState(initialNextCursor)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  useEffect(() => {
    setItems(initialItems)
    setTotal(initialTotal)
    setNextCursor(initialNextCursor)
    setLoadError('')
    setIsLoadingMore(false)
    loadingRequest.current = null
  }, [appliedFilter, initialItems, initialNextCursor, initialTotal])

  const hasMore = Boolean(nextCursor)

  const loadMore = useCallback(async () => {
    if (!nextCursor || isLoadingMore || loadingRequest.current || loadError)
      return
    const request = { filter: currentFilter.current }
    loadingRequest.current = request

    setIsLoadingMore(true)

    try {
      const result = await loadMoreCommunities({
        gameName,
        filter: appliedFilter,
        cursor: nextCursor,
      })

      if (
        request.filter !== currentFilter.current ||
        loadingRequest.current !== request
      )
        return
      setItems((previous) => [...previous, ...result.items])
      setTotal(result.total)
      setNextCursor(result.nextCursor)
    } catch {
      if (loadingRequest.current === request)
        setLoadError('Could not load more results. Please try again.')
    } finally {
      if (loadingRequest.current === request) {
        loadingRequest.current = null
        setIsLoadingMore(false)
      }
    }
  }, [appliedFilter, gameName, isLoadingMore, nextCursor, loadError])

  useEffect(() => {
    if (!loaderRef.current || !hasMore) return

    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore()
      },
      { threshold: 0.2 },
    )

    observer.observe(loaderRef.current)

    return () => observer.disconnect()
  }, [hasMore, loadMore])

  return (
    <>
      <GameDiscoverySearchForm
        game={game}
        appliedFilter={appliedFilter}
        mode="communities"
      />

      <div className="col-span-full mt-6 grid gap-5 desktop-xs:col-span-10 desktop-xs:col-start-2 desktop-xs:grid-cols-2 desktop-sm:grid-cols-3 desktop-lg:gap-9">
        {items.length > 0 ? (
          items.map((community) => (
            <div key={community.id}>
              <CommunityCard community={community} />
            </div>
          ))
        ) : (
          <p className="col-span-full">No communities found.</p>
        )}

        {loadError && (
          <div role="alert" className="col-span-full">
            {loadError}{' '}
            <Button onClick={() => setLoadError('')}>
              Retry loading results
            </Button>
          </div>
        )}
        {hasMore && (
          <div
            ref={loaderRef}
            className="col-span-full flex justify-center py-4"
          >
            {isLoadingMore ? (
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-neutral-blue-700 border-t-primary-500" />
            ) : (
              <Button variant="secondary" onClick={loadMore}>
                Load more
              </Button>
            )}
          </div>
        )}
      </div>

      <p
        className={cn(
          'col-span-full mt-6 text-right text-p-2 text-neutral-blue-300 desktop-xs:col-span-10 desktop-xs:col-start-2',
          total === 0 && 'hidden',
        )}
      >
        Showing {items.length} of {total}
      </p>
    </>
  )
}
